import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import Api from '@mui/icons-material/Api';
import Update from '@mui/icons-material/Update';
import UpdateDisabled from '@mui/icons-material/UpdateDisabled';

import { DateTime } from 'luxon';
import { useQuery } from '@tanstack/react-query';

import { dashboardAPI } from '../../../utils/dashboardAPI.js';

import { useAuth } from '../../../AuthContext'
import { getStatusColor } from '../../../utils/formatters';

const StatusOverview = ({ autoRefresh }) => {
    const { token } = useAuth();

    // queue status updates frequently: poll every 5s, short stale time
    const { data: queueStatusData, isFetching: queueFetching } = useQuery({
        queryKey: [ 'queueStatus' ],
        queryFn: () => dashboardAPI.getQueueStatus(token),
        staleTime: 5000,
        retry: 1,
        refetchInterval: autoRefresh ? 5000 : false
    });

    // system status is less volatile: poll every 30s
    const { data: systemStatusData, isFetching: systemFetching } = useQuery({
        queryKey: [ 'systemStatus' ],
        queryFn: () => dashboardAPI.getSystemStatus(token),
        staleTime: 30000,
        retry: 1,
        refetchInterval: autoRefresh ? 30000 : false
    });

    const queueStatus = queueStatusData || {};
    const systemStatus = systemStatusData || {};

    // Sum only numeric fields (ignore apiVersion or other non-numeric metadata)
    const queueTotal = Object.values(queueStatus || {}).reduce((sum, v) => {
        return sum + (typeof v === 'number' ? v : 0);
    }, 0);

    return (
        <Grid container spacing={ 3 } sx={ { mb: 3 } }>
            <Grid size={ { xs: 12, sm: 4 } }>
                <Card sx={ { height: '100%' } }>
                    <CardContent>
                        <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                            <Typography variant="h6" gutterBottom>
                                Queue Status
                            </Typography>
                            { queueFetching && (
                                <Chip label="Updating…" size="small" icon={ <CircularProgress size={ 12 } /> } />
                            ) }
                        </Box>
                        <Box>
                            <Grid container spacing={ 1 }>
                                <Grid size={ { xs: 6, sm: 12 } }>
                                    <Chip
                                        label={ `Pending: ${ queueStatus?.pending || 0 }` }
                                        color={ getStatusColor('pending') }
                                        size="small"
                                    />
                                </Grid>
                                <Grid size={ { xs: 6, sm: 12 } }>
                                    <Chip
                                        label={ `Processing: ${ queueStatus?.processing || 0 }` }
                                        color={ getStatusColor('processing') }
                                        size="small"
                                    />
                                </Grid>
                                <Grid size={ { xs: 6, sm: 12 } }>
                                    <Chip
                                        label={ `Sent: ${ queueStatus?.sent || 0 }` }
                                        color={ getStatusColor('sent') }
                                        size="small"
                                    />
                                </Grid>
                                <Grid size={ { xs: 6, sm: 12 } }>
                                    <Chip
                                        label={ `Failed: ${ queueStatus?.failed || 0 }` }
                                        color={ getStatusColor('failed') }
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                            <Typography variant="body2" color="textSecondary" sx={ { mt: 2 } }>
                                Total: { queueTotal } messages
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            <Grid size={ { xs: 12, sm: 4 } }>
                <Card sx={ { height: '100%' } }>
                    <CardContent>
                        <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                            <Typography variant="h6" gutterBottom>
                                System Status
                            </Typography>

                            { systemFetching && (
                                <Chip label="Updating…" size="small" icon={ <CircularProgress size={ 12 } /> } />
                            ) }
                        </Box>
                        <Box>
                            <Grid container spacing={ 1 } sx={ { mb: 1 } }>
                                <Chip
                                    icon={ systemStatus?.schedulerRunning ? <Update fontSize="small" /> : <UpdateDisabled fontSize="small" /> }
                                    label={ systemStatus?.schedulerRunning ? 'Scheduler Running' : 'Scheduler Stopped' }
                                    color={ systemStatus?.schedulerRunning ? 'success' : 'error' }
                                    size="small"
                                />

                                <Chip
                                    icon={ <Api fontSize="small" /> }
                                    label={ systemStatus?.apiVersion }
                                    color={ systemStatus?.apiVersion ? 'secondary' : 'error' }
                                    size="small"
                                />
                            </Grid>

                            <Typography variant="body2" gutterBottom>
                                Last Process: { DateTime.fromMillis(systemStatus?.lastProcessTime || 0).toLocaleString(DateTime.DATETIME_MED) || 'N/A' }
                            </Typography>

                            <Typography variant="body2" gutterBottom>
                                Uptime: { systemStatus?.uptime || 'N/A' }
                            </Typography>

                            <Typography variant="body2">
                                Monitored Shifts: { systemStatus?.monitoredShifts || 0 }
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            <Grid size={ { xs: 12, sm: 4 } }>
                <Card sx={ { height: '100%' } }>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Performance
                        </Typography>
                        <Box>
                            <Typography variant="body2" gutterBottom>
                                Messages/Hour: { systemStatus?.messagesPerHour || 0 }
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                Success Rate: { systemStatus?.successRate || 0 }%
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                Avg Process Time: { systemStatus?.avgProcessTime || 0 }s
                            </Typography>
                            <Typography variant="body2">
                                Queue Size: { systemStatus?.queueSize || 0 }
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default StatusOverview;
